import { axiosInstance } from '../App';
import { unreadUpdates } from '../lib/whatsNew';
import { canAny, isAdminOrPlatform, isPlatform } from '../lib/access';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Search, MessageCircle, FileText, Users, BarChart3,
  Network, Link2, LogOut, ShieldHalf,
  PanelLeftClose, BookOpen, ShieldCheck
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import { getRoleLabel } from '../lib/roleLabels';
import WelcomeTour from './WelcomeTour';
import QuickAsk from './QuickAsk';
import AuthImage from './AuthImage';

const NAV_GROUPS = ['Work', 'Insights', 'Organisation', 'Admin'];

const navItems = [
  { path: '/', label: 'Home', testId: 'dashboard', icon: Home, group: 'Work' },
  { path: '/query', label: 'Ask Qflow', testId: 'submit-query', icon: Search, group: 'Work' },
  { path: '/floor-space', label: 'Floor Space', icon: MessageCircle, group: 'Work' },
  { path: '/resolutions', label: 'Knowledge Hub', icon: FileText, group: 'Work' },
  { path: '/analytics', label: 'Analytics', icon: BarChart3, group: 'Insights' },
  { path: '/team-users', label: 'Team & Users', icon: Users, group: 'Organisation', allow: (user) => isPlatform(user) || canAny(user) },
  { path: '/org-chart', label: 'Org Chart', icon: Network, group: 'Organisation' },
  { path: '/integrations', label: 'Plugins', icon: Link2, group: 'Admin', allow: isAdminOrPlatform },
  // Organisation settings for the Tenant Admin, the way Platform Operations is for the platform owner.
  { path: '/admin', label: 'Admin', icon: ShieldHalf, group: 'Admin', allow: isAdminOrPlatform },
  { path: '/platform-operations', label: 'Platform Operations', icon: ShieldCheck, group: 'Admin', roles: ['master_admin'] },
  { path: '/getting-started', label: 'Learn Qflow', icon: BookOpen, guidance: true },
];

// White-labelled organisations lead with their own initials; a small Q badge keeps Qflow identifiable.
const brandInitials = (name) => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 'Q';
  return (words.length > 1 ? words[0][0] + words[1][0] : words[0].slice(0, 2)).toUpperCase();
};
const BrandMark = ({ name }) => (name
  ? <span className="qflow-org-mark h-8 w-8 text-[13px]" title={`${name} · Powered by Qflow`} data-testid="sidebar-org-mark">
      <span aria-hidden="true">{brandInitials(name)}</span>
      <span className="qflow-org-mark-badge" aria-hidden="true">Q</span>
      <span className="sr-only">Powered by Qflow</span>
    </span>
  : <span className="qflow-brand-mark h-8 w-8 text-sm" aria-hidden="true">Q</span>);

const navTestId = (item) => `sidebar-nav-${item.testId || item.label.toLowerCase().replace(/\s+/g, '-')}`;

const SidebarLayout = ({ user, setUser, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [learning, setLearning] = useState(user?.learning || {});
  const [guidanceUnseen, setGuidanceUnseen] = useState(0);
  const [organizationBranding, setOrganizationBranding] = useState(user?.organization_branding || {});
  const [userCollapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('qflow_sidebar_collapsed') === 'true';
  });

  // Phones and narrow windows always get the icon rail so content keeps its width.
  const [narrow, setNarrow] = useState(() => window.matchMedia?.('(max-width: 767px)').matches || false);
  useEffect(() => {
    const query = window.matchMedia?.('(max-width: 767px)');
    if (!query) return undefined;
    const update = (event) => setNarrow(event.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    localStorage.setItem('qflow_sidebar_collapsed', userCollapsed);
  }, [userCollapsed]);
  const collapsed = userCollapsed || narrow;
  useEffect(() => {
    const updateBranding = event => setOrganizationBranding(event.detail || {});
    window.addEventListener('qflow:organization-branding-updated', updateBranding);
    return () => window.removeEventListener('qflow:organization-branding-updated', updateBranding);
  }, []);
  useEffect(() => {
    let sessionId = sessionStorage.getItem('qflow_session_id');
    if (!sessionId) {
      sessionId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem('qflow_session_id', sessionId);
    }
    axiosInstance.post('/activity/track', {
      event_type: 'page_view', page: location.pathname, session_id: sessionId,
      metadata: { source: 'sidebar' },
    }).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const refresh = () => axiosInstance.get('/guidance').then(response => setGuidanceUnseen(response.data.unseen_count || 0)).catch(() => {});
    refresh();
    axiosInstance.get('/users/me/learning').then(({ data }) => setLearning(data)).catch(() => {});
    const update = (event) => setLearning(event.detail || {});
    window.addEventListener('qflow:learning-updated', update);
    window.addEventListener('qflow:guidance-updated', refresh);
    return () => { window.removeEventListener('qflow:learning-updated', update); window.removeEventListener('qflow:guidance-updated', refresh); };
  }, [user?.id, location.pathname]);
  const learnUnread = guidanceUnseen + unreadUpdates(user, learning).length;

  const handleLogout = async () => {
    try { await axiosInstance.post('/auth/logout'); } catch (_) { /* local logout must still complete */ }
    localStorage.removeItem('token');
    setUser(null);
    navigate('/auth');
  };

  const filteredItems = navItems.filter(item => {
    if (item.path === '/floor-space' && user.permissions?.can_view_floor_space === false) return false;
    if (item.path === '/platform-operations') return user.is_master_admin === true;
    if (item.allow) return item.allow(user);
    return !item.roles || item.roles.includes(user.role);
  });
  const tenantDisplayName = organizationBranding?.enabled && organizationBranding?.display_name
    ? String(organizationBranding.display_name).trim()
    : '';
  const isAdministrator = isAdminOrPlatform(user);

  const renderItem = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <button
        key={item.path}
        type="button"
        onClick={() => navigate(item.path)}
        data-testid={navTestId(item)}
        title={collapsed ? item.label : undefined}
        aria-label={collapsed ? item.label : undefined}
        aria-current={isActive ? 'page' : undefined}
        className={`qflow-nav-item flex w-full items-center gap-3 text-sm transition-colors ${
          collapsed ? 'h-10 justify-center px-2' : 'min-h-[40px] px-3'
        }`}
      >
        <Icon size={18} className="flex-shrink-0" aria-hidden="true" />
        {!collapsed && (
          <span className="flex flex-1 items-center justify-between">
            {item.label}
            {item.path === '/query' && <kbd className="ml-2 rounded border px-1.5 text-[10px] font-semibold" style={{ borderColor: 'var(--qflow-border)', color: 'var(--qflow-sidebar-muted)' }} title="Press / on any page to ask">/</kbd>}
            {item.guidance && learnUnread > 0 && <span className="qflow-accent-bg ml-2 rounded-full px-2 py-0.5 text-[11px] font-bold">{learnUnread} new</span>}
          </span>
        )}
      </button>
    );
  };

  const guidanceItem = filteredItems.find(item => item.guidance);

  return (
    <div className="flex h-screen" style={{ background: 'var(--qflow-app-bg)' }} data-testid="sidebar-layout">
      <aside
        className={`flex flex-shrink-0 flex-col border-r transition-all duration-200 ease-in-out ${collapsed ? 'w-16' : 'w-60'}`}
        style={{ background: 'var(--qflow-sidebar-bg)', color: 'var(--qflow-sidebar-text)', borderColor: 'var(--qflow-border)' }}
        data-testid="sidebar"
      >
        <div className={`flex h-16 items-center px-3 ${collapsed ? 'justify-center' : 'justify-between gap-2'}`}>
          {!collapsed && (
            <div className="flex min-w-0 items-center gap-2.5 pl-1" data-testid="sidebar-title">
              <BrandMark name={tenantDisplayName} />
              <span className="block min-w-0 truncate text-base font-extrabold" style={{ color: 'var(--qflow-sidebar-strong)' }}>{tenantDisplayName || 'Qflow'}</span>
            </div>
          )}
          {narrow && <BrandMark name={tenantDisplayName} />}
          {!narrow && <button
            type="button"
            onClick={() => setCollapsed(!userCollapsed)}
            className={collapsed ? "grid h-10 w-10 place-items-center rounded-lg" : "qflow-nav-item grid h-8 w-8 place-items-center"}
            data-testid="sidebar-toggle"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? `${tenantDisplayName || 'Qflow'}${tenantDisplayName ? ' · Powered by Qflow' : ''} · Expand sidebar` : 'Collapse sidebar'}
          >
            {collapsed ? <BrandMark name={tenantDisplayName} /> : <PanelLeftClose size={16} />}
          </button>}
        </div>

        <nav aria-label="Main" className="flex-1 space-y-0.5 overflow-y-auto px-2 pb-2">
          {NAV_GROUPS.map(group => {
            const items = filteredItems.filter(item => item.group === group);
            if (!items.length) return null;
            return (
              <div key={group} className="space-y-0.5">
                {collapsed
                  ? <div className="mx-3 my-2 border-t" style={{ borderColor: 'var(--qflow-border)' }} aria-hidden="true" />
                  : <p className="qflow-nav-group">{group}</p>}
                {items.map(renderItem)}
              </div>
            );
          })}
        </nav>

        <div className={`space-y-1 border-t p-2 ${collapsed ? 'flex flex-col items-center' : ''}`} style={{ borderColor: 'var(--qflow-border)' }}>
          {guidanceItem && renderItem(guidanceItem)}
          {isAdministrator && <NotificationBell user={user} collapsed={collapsed} />}
          {!collapsed && (
            <button type="button" onClick={() => navigate('/profile')} title="Your profile" data-testid="sidebar-user-button" className="qflow-nav-item flex w-full items-center gap-2.5 px-2 py-2 text-left">
              <span className="grid h-8 w-8 flex-shrink-0 place-items-center overflow-hidden rounded-full text-xs font-bold" style={{ background: 'var(--qflow-accent-soft)', color: 'var(--qflow-accent-strong)' }}>
                <AuthImage src={user.photo_url} className="h-full w-full object-cover" fallback={user.name?.charAt(0)?.toUpperCase() || 'U'} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold" style={{ color: 'var(--qflow-sidebar-strong)' }} data-testid="sidebar-user-name">{user.name}</span>
                <span className="block text-[11px]" style={{ color: 'var(--qflow-sidebar-muted)' }}>{getRoleLabel(user.role, user)}</span>
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={handleLogout}
            title="Logout"
            aria-label={collapsed ? 'Logout' : undefined}
            data-testid="sidebar-logout-btn"
            className={`qflow-nav-item flex items-center gap-2 text-sm hover:!text-red-600 ${collapsed ? 'h-10 w-10 justify-center' : 'w-full px-3 py-2'}`}
          >
            <LogOut size={16} aria-hidden="true" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto" data-testid="sidebar-main-content">
        {children}
      </main>
      <WelcomeTour user={user} setUser={setUser} />
      <QuickAsk />
    </div>
  );
};

export default SidebarLayout;
